import React, { useState } from 'react';
import pb from '../pb';

export default function Login({ onLogin, onDemo }) {
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  function switchMode(next) {
    setMode(next);
    setError('');
    setPassword('');
    setConfirmPw('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (mode === 'signup' && password !== confirmPw) {
      setError('Passwords do not match.');
      return;
    }
    if (mode === 'signup' && password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const users = pb.collection('users');
      if (mode === 'signup') {
        // New accounts always start as a regular "user". An admin can
        // promote them later from the Users page.
        await users.create({ email, password, passwordConfirm: password, role: 'user' });
      }
      await users.authWithPassword(email, password);
      onLogin();
    } catch (err) {
      const msg = (err?.message || '').toLowerCase();
      if (mode === 'signin') {
        setError('Invalid email or password.');
      } else if (msg.includes('email') && (msg.includes('exist') || msg.includes('taken') || msg.includes('valid'))) {
        setError('An account with this email already exists.');
      } else {
        setError('Account creation failed. Check the email and try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  const isSignUp = mode === 'signup';

  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* Brand mark */}
        <div style={s.brand}>
          <div style={s.brandMark}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect width="20" height="20" rx="6" fill="#4F46E5"/>
              <path d="M5 7h10M5 10h7M5 13h5" stroke="white" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </div>
          <span style={s.brandName}>denim<span style={s.brandAccent}>erp</span></span>
        </div>

        <div style={s.heading}>{isSignUp ? 'Create account' : 'Welcome back'}</div>
        <div style={s.sub}>
          {isSignUp ? 'Set up your production dashboard access' : 'Sign in to your production dashboard'}
        </div>

        <form onSubmit={handleSubmit} style={s.form}>
          <div style={s.field}>
            <label style={s.label}>Email address</label>
            <input
              style={s.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoComplete="email"
              onFocus={e => { e.target.style.borderColor = '#4F46E5'; e.target.style.boxShadow = '0 0 0 3px rgba(79,70,229,0.1)'; }}
              onBlur={e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={s.field}>
            <label style={s.label}>Password</label>
            <div style={s.pwWrap}>
              <input
                style={{ ...s.input, paddingRight: 44 }}
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                onFocus={e => { e.target.style.borderColor = '#4F46E5'; e.target.style.boxShadow = '0 0 0 3px rgba(79,70,229,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; }}
              />
              <button type="button" style={s.pwToggle} onClick={() => setShowPw(v => !v)}>
                {showPw ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          {isSignUp && (
            <div style={s.field}>
              <label style={s.label}>Confirm password</label>
              <input
                style={s.input}
                type={showPw ? 'text' : 'password'}
                value={confirmPw}
                onChange={e => setConfirmPw(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                onFocus={e => { e.target.style.borderColor = '#4F46E5'; e.target.style.boxShadow = '0 0 0 3px rgba(79,70,229,0.1)'; }}
                onBlur={e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; }}
              />
            </div>
          )}

          {error && (
            <div style={s.errorBox}>
              <span style={s.errorIcon}>⚠</span> {error}
            </div>
          )}

          <button
            style={{ ...s.submitBtn, opacity: loading ? 0.75 : 1 }}
            type="submit"
            disabled={loading}
          >
            {loading ? (isSignUp ? 'Creating…' : 'Signing in…') : (isSignUp ? 'Create account' : 'Sign in')}
          </button>
        </form>

        <div style={s.switchRow}>
          {isSignUp ? (
            <>Already have an account?{' '}
              <button style={s.switchBtn} onClick={() => switchMode('signin')}>Sign in</button>
            </>
          ) : (
            <>New here?{' '}
              <button style={s.switchBtn} onClick={() => switchMode('signup')}>Create account</button>
            </>
          )}
        </div>

        {onDemo && (
          <div style={s.switchRow}>
            Just looking around?{' '}
            <button style={s.switchBtn} onClick={onDemo}>Try the demo</button>
          </div>
        )}
      </div>

      <div style={s.footer}>
        Production Management System · Denim ERP
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #0F172A 0%, #1E1B4B 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: "'DM Sans', sans-serif",
  },
  card: {
    background: '#FFFFFF',
    borderRadius: 20,
    padding: '40px 40px 36px',
    width: '100%',
    maxWidth: 420,
    boxShadow: '0 24px 48px rgba(0,0,0,0.28), 0 8px 16px rgba(0,0,0,0.12)',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 28,
  },
  brandMark: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  brandName: {
    fontSize: 22,
    fontWeight: 800,
    color: '#111827',
    letterSpacing: '-0.04em',
  },
  brandAccent: { color: '#4F46E5' },
  heading: {
    fontSize: 26,
    fontWeight: 700,
    color: '#111827',
    letterSpacing: '-0.02em',
    marginBottom: 6,
  },
  sub: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 28,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    color: '#374151',
  },
  input: {
    width: '100%',
    border: '1.5px solid #E5E7EB',
    borderRadius: 10,
    padding: '11px 14px',
    fontSize: 15,
    color: '#111827',
    background: '#FAFAFA',
    outline: 'none',
    boxSizing: 'border-box',
  },
  pwWrap: { position: 'relative' },
  pwToggle: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    padding: 2,
  },
  errorBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    color: '#DC2626',
    fontWeight: 500,
  },
  errorIcon: { fontSize: 14 },
  submitBtn: {
    background: '#4F46E5',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '13px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
    letterSpacing: '-0.01em',
  },
  switchRow: {
    marginTop: 20,
    textAlign: 'center',
    fontSize: 13,
    color: '#6B7280',
  },
  switchBtn: {
    background: 'none',
    border: 'none',
    color: '#4F46E5',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    padding: 0,
    fontFamily: "'DM Sans', sans-serif",
  },
  footer: {
    marginTop: 24,
    fontSize: 12,
    color: 'rgba(255,255,255,0.3)',
    textAlign: 'center',
  },
};
